import normalize from 'json-api-normalizer';
import Question from './question';
import Page from './page';
import { QuestionTypes, QuestionParams } from '@/lib/entities';
import QuestionParamValue from './question_param_value';
import QuestionChoice from './question_choice';

export default class Survey {
	static from (jsonapi) {
		let key = 'surveys'
		let normalizedJSONapi = normalize(jsonapi);
		if (!(Object.keys(normalizedJSONapi).includes(key))) return null;

		let keys = Object.keys(normalizedJSONapi[key]);
		if (keys.length > 0 && Array.isArray(jsonapi.data)) {
			let retVal = {};
			keys.forEach( function ( id ) {
				retVal[id] = Survey.make(normalizedJSONapi[key][id], normalizedJSONapi);
			});
			return retVal;
		} else return Survey.make(normalizedJSONapi[key][keys[0]], normalizedJSONapi);
	}

	static make(jsonapi, normalizedJSONapi, noRelationships=0) {
		const { id, type, attributes } = jsonapi;

		let relationships = {}
		if (jsonapi.relationships && jsonapi.relationships['questions'] && normalizedJSONapi['questions'] && noRelationships == 0) {
			relationships['questions'] = {};
			jsonapi.relationships['questions'].data.forEach( function (rel) {
				relationships['questions'][rel.id] = Question.make(normalizedJSONapi[rel.type][rel.id], normalizedJSONapi);
			});
		}

		if ('pageId' in attributes && attributes['pageId'] > 0 && normalizedJSONapi['pages'] && noRelationships == 0) {
			relationships['page'] = Page.make(normalizedJSONapi['pages'][attributes['pageId']], normalizedJSONapi);
		}

		return new Survey ( Object.assign({id: id, type: type}, attributes, relationships) );
	}

	constructor ({ id, title, state, questions, audience, pageId, page, type }) {
		this.id         = id ? id : -1;
		this.title      = title ? title : this._guid();
		this.audience   = audience ? audience : 'client'
		this.state      = state ? state : 'draft';
		this.questions  = questions ? questions : {};
		this.page_id    = pageId ? pageId : -1;
		this.page       = page ? page : null;
		this.type       = type ? type : 'surveys';
	}

	get internal_name() {
		return this.title;
	}

	set internal_name(value) {
		this.title = value;
	}

	static fromSurveyJS( json ) {
		let id = ('_id' in json ) ? json['_id'] : -1
		let survey = {
			id:    id,
		    state: ('_state' in json) ? json['_state'] : 'draft',
			title: json['title'] ? json['title'] : this._guid(),
			audience: json['audience'] ? json['audience'] : 'client',
			pageId: ('_pageID' in json) ? Number.parseInt(json['_pageID']) : -1,
			questions: {}
		}

		if ('pages' in json) {
			if (json['pages'].length > 0) {
				if ('elements' in json['pages'][0]) {
					for (let index = 0; index < json['pages'][0]['elements'].length; index++) {
						let element = json['pages'][0]['elements'][index];

						let questionId = ('_id' in element) ? element['_id'] : (-1 * (index+1));
						let questionType = QuestionTypes[element['type']];
						let options = {}

						if (questionType['include_choices']) {
							let choices = element[questionType['choices_name']];
							if (choices) {

								for ( let choiceIndex = 0; choiceIndex < choices.length; choiceIndex++ ) {

									if (typeof choices[choiceIndex] == 'object') {
										let chid = ('_id' in choices[choiceIndex] && id > 0) ? choices[choiceIndex]['_id'] : (-1 * (choiceIndex + 1))
										options[chid] = new QuestionChoice( { id: chid, questionId, value: choices[choiceIndex]['value'], text: choices[choiceIndex]['text']});
									} else {
										let chid = (-1 * (choiceIndex + 1))
										options[chid] = new QuestionChoice( { id: chid, questionId, value: choices[choiceIndex], text: choices[choiceIndex] } );
									}
								}
							}
						}

						let rows = {};
						if ('rows' in element) {
							let children = element['rows']
							if (children) {
								for ( let childrenIndex = 0; childrenIndex < children.length; childrenIndex++ ) {

									if (typeof children[childrenIndex] == 'object') {
										let chid = ('_id' in children[childrenIndex] && id > 0) ? children[childrenIndex]['_id'] : (-1 * (childrenIndex + 1))
										rows[chid] = new Question( { id: chid, questionType: QuestionTypes['row'], number: childrenIndex, surveyId: ('_id' in json  && id > 0) ? json['_id'] : -1, varname: children[childrenIndex]['value'], title: children[childrenIndex]['text']});
									} else {
										let chid = (-1 * (childrenIndex + 1))
										rows[chid] = new Question( { id: chid, questionType: QuestionTypes['row'], number: childrenIndex, surveyId: ('_id' in json  && id > 0) ? json['_id'] : -1, varname: children[childrenIndex], title: children[childrenIndex] } );
									}

								}
							}
						}

						let questionParamValues = {};
						let keys = Object.keys(element);

						for (let keyIndex = 0; keyIndex < keys.length; keyIndex++)
						{
							if ( keys[keyIndex] in QuestionParams) {
								questionParamValues[-1 * keyIndex] = new QuestionParamValue ({ id: -1 * keyIndex, questionId, questionParam: QuestionParams[keys[keyIndex]], value: element[keys[keyIndex]] });
							}
						}

						let html = (element.type == 'html') ? element.html : '';

						let question = new Question({
							id:                  questionId,
							questionType:        questionType,
							number:              (index+1),
							title:               element['title'],
							varname:             element['name'],
							surveyId:            ('_id' in json  && id > 0) ? json['_id'] : -1,
							questionParamValues: questionParamValues,
							questionChoices:     options,
							rows:                rows,
							html:				 html,
						});

						survey['questions'][question.id] = question;
					}
				}
			}
		}

		return new Survey ( survey );
	}

	toSurveyJS (includeId = true, display_title) {

		let questions = _.cloneDeep(this.questions);
		questions = _.orderBy(questions, 'number');

		let surveyJS = {}
		if (includeId) { surveyJS['_id'] = this.id; }
		surveyJS['_state'] = this.state;
		if (this.page) {
			surveyJS['_pageID'] = this.page_id;
		}
		surveyJS['audience'] = this.audience;
		surveyJS['title'] = ((includeId) ? ((display_title != null && display_title.trim().length > 0)?display_title:this.title) : this._guid());
		surveyJS['isSinglePage'] = 'true';
		surveyJS['showPageTitles'] = 'false'

		let elements = [];
		let keys = Object.keys(questions);
		let keys2 = {};
		for (let index = 0; index < keys.length; index++) {
			let q = questions[keys[index]];

			let data = {
				'_number': q.number,
				'type':    q.type,
				'name':    q.varname,
				'title':   q.title,
				'html':	   q.html,
			};

			if (includeId) {
				data['_id'] = q.id
			}

			keys2 = Object.keys(q.choices)
			if (keys2.length > 0) {
				data['choices'] = [];

				for (let index2 = 0; index2 < keys2.length; index2++) {
					let choiceData = {
						'text':  q.choices[keys2[index2]].text,
						'value': q.choices[keys2[index2]].value
					};

					if (includeId) {
						choiceData['_id'] = q.choices[keys2[index2]].id;
					}

					data['choices'].push(choiceData);
				}
			}

			keys2 = Object.keys(q.rate_values);
			if (keys2.length > 0) {
				data['rateValues'] = [];

				for (let index2 = 0; index2 < keys2.length; index2++) {
					let rateData = {
						'text':  q.rate_values[keys2[index2]].text,
						'value': q.rate_values[keys2[index2]].value
					};

					if (includeId) {
						rateData['_id'] = q.rate_values[keys2[index2]].id;
					}

					data['rateValues'].push(rateData);
				}
			}


			keys2 = Object.keys(q.columns);
			if (keys2.length > 0) {
				data['columns'] = [];

				for (let index2 = 0; index2 < keys2.length; index2++) {
					let column = {
						'text':  q.columns[keys2[index2]].text,
						'value': String(q.columns[keys2[index2]].value)				
					};

					if (includeId) {
						column['_id'] = q.columns[keys2[index2]].id;
					}

					data['columns'].push(column);
				}
			}

			keys2 = Object.keys(q.rows);
			var new_rows = _.orderBy(q.rows, 'number');
			if (keys2.length > 0) {
				data['rows'] = [];

				for (let index2 = 0; index2 < keys2.length; index2++) {
					let column = {
						// 'text':  q.rows[keys2[index2]].title,
						// 'value': q.rows[keys2[index2]].varname
						'text': new_rows[index2].title,
						'value': new_rows[index2].varname	
					};

					if (includeId) {
						// column['_id'] = keys2[index2];
						column['_id'] = Number(new_rows[index2].id);
					}

					data['rows'].push(column);
				}
			}

			keys2 = Object.keys(q.params)
			if (keys.length > 0) {
				for (let index2 = 0; index2 < keys2.length; index2++) {
					data[q.params[keys2[index2]].question_param.name] = (q.params[keys2[index2]].question_param.convert_to_bool)?(q.params[keys2[index2]].value != 0) : q.params[keys2[index2]].value ;
				}
			}

			// If Matrix Question Required, set all rows as required
			if (data.type == 'matrix' && data.isRequired) data['isAllRowRequired'] = true;

			elements.push(data);
		}

		surveyJS['pages'] = [ { 'name': 'page1', 'elements': elements }]

		return JSON.stringify(surveyJS);
	}

	_guid() {
		function s4() {
			return Math.floor((1 + Math.random()) * 0x10000)
					 .toString(16)
					 .substring(1);
		}
		return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();
	}

	toJSONapi (fullTranslation = true) {
		let attributes = Object.assign({}, this);
		delete attributes.id;

		if (attributes['page_id'] < 0) {
			attributes['page_id'] = null;
		}

		return {data: {type: "surveys", id: this.id, attributes} }
	}
}
